import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { financeJournalAPI, customersAPI } from "../../../../services";
import "../../../../style/journalView.css";

import DeleteIcon from "../../../../assets/delete-modal-icon.svg";
import DailyReportIcon from "../../../../assets/icons/DailyReportIcon.js";
import CreditIcon from "../../../../assets/icons/CreditIcon.js";
import DebitIcon from "../../../../assets/icons/DebitIcon.js";
import EditIcon from "../../../../assets/icons/EditIcon.js";
import RedDeleteIcon from "../../../../assets/icons/RedDeleteIcon.js";
import FilterIcon from "../../../../assets/icons/FilterIcon.js";
import NoDataIcon from "../../../../assets/icons/NoDataIcon.js";
import JournalEditModal from "./JournalEditModal.js";
import ArrowDownUpIcon from "../../../../assets/icons/ArrowDownUpIcon.js";

import DailyReportView from "./DailyReportView.js";
import { useSelector } from "react-redux";
import { Calendar, ChevronDown, Search, Check } from "lucide-react";

function JournalView() {
  const { isPartsURL, isAccessoriesURL } = useSelector((state) => state.domain);
  const [formData, setFormData] = useState({
    date: new Date().toLocaleDateString("en-GB"),
    customerId: "",
    note: "",
    debit: "",
    credit: "",
  });
  const [, setLastUsedDate] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [, setTotalPages] = useState(1);
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const datePickerRef = useRef(null);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [balanceFilter, setBalanceFilter] = useState("all");
  const filterDropdownRef = useRef(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingTransaction, setDeletingTransaction] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [transactionsList, setTransactionsList] = useState([]);
  const [showDailyReport, setShowDailyReport] = useState(false);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const isFirstRender = useRef(true);
  const isCRUDOperation = useRef(false);
  const journalsFetching = useRef(false);
  const transactionsSectionRef = useRef(null);
  const tableBodyRef = useRef(null);
  const scrollCleanupRef = useRef(null);
  const [isMainScrollLocked, setIsMainScrollLocked] = useState(false);
  const [, setShowPagination] = useState(false);
  const customerSearchInputRef = useRef(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  
  // Sorting state: null = original, 'asc' = ascending, 'desc' = descending
  const [customerSort, setCustomerSort] = useState(null);
  const [amountSort, setAmountSort] = useState(null);
  
  // Fetch customers from API with search parameter
  const fetchCustomers = async (searchQuery = "") => {
    if (customersLoading) return;

    setCustomersLoading(true);
    try {
      const { response } = await customersAPI.getCustomers(searchQuery, 1, 100);

      let customerData = [];
      if (response && response.data && Array.isArray(response.data)) {
        customerData = response.data;
      } else if (response && Array.isArray(response)) {
        customerData = response;
      }

      const toNullableNumber = (value) => {
        if (value === null || value === undefined || value === "") return null;
        const amount = Number(value);
        if (Number.isNaN(amount) || !Number.isFinite(amount)) return null;
        return amount;
      };

      const transformedCustomers = customerData.map((customer) => {
        return {
          id: customer.id.toString(),
          name:
            customer.name ||
            customer.email ||
            customer.full_name ||
            `Customer ${customer.id}`,
          // null = no limit (skip check); otherwise numeric (e.g. "0.00" -> 0)
          credit_limit: toNullableNumber(customer.credit_limit),
          balance: toNullableNumber(customer.balance) ?? 0,
        };
      });

      setCustomers(transformedCustomers);
    } catch (error) {
      setCustomers([]);
    } finally {
      setCustomersLoading(false);
    }
  };

  // Fetch journals from API
  const fetchJournals = async () => {
    // Prevent multiple simultaneous calls
    if (loading || journalsFetching.current) {
      return;
    }
    journalsFetching.current = true;
    setLoading(true);
    try {
      // Prepare API parameters
      const apiParams = {};

      // Add search parameter
      if (searchTerm) {
        apiParams.search = searchTerm;
      }

      // Always add today's date in the required format
      const today = new Date().toISOString().split("T")[0]; // Format: YYYY-MM-DD
      apiParams.from = today;
      apiParams.to = today;

      // Add transaction type filter
      if (balanceFilter !== "all") {
        apiParams.type = balanceFilter; // 'debit' or 'credit'
      }

      const response = await financeJournalAPI.getJournalsList(apiParams);

      let journals = [];
      if (response && response.data && Array.isArray(response.data)) {
        journals = response.data;
      } else if (Array.isArray(response)) {
        journals = response;
      } else if (response && Array.isArray(response.results)) {
        journals = response.results;
      }

      // Transform API data to match table structure
      const transformedJournals = journals.map((journal, index) => {
        // Find customer name by ID
        const customerName =
          customers.find((c) => c.id === journal.customer?.toString())?.name ||
          journal.customer_name ||
          `Customer ${journal.customer}` ||
          "Unknown Customer";

        const transformed = {
          id: journal.tnx_id || journal.id,
          customer: customerName,
          note: journal.note || "No note",
          credit:
            journal.tnx_type === "credit" ? parseFloat(journal.amount || 0) : 0,
          debit:
            journal.tnx_type === "debit" ? parseFloat(journal.amount || 0) : 0,
          balance: parseFloat(journal.balance || 0),
          date: journal.tnx_date
            ? new Date(journal.tnx_date).toLocaleDateString("en-GB")
            : new Date().toLocaleDateString("en-GB"),
          // Keep original data for editing
          originalData: journal,
        };
        return transformed;
      });

      setTransactionsList(transformedJournals);
      setTotalPages(Math.ceil(transformedJournals.length / itemsPerPage));
    } catch (error) {
      toast.error("Failed to fetch journals. Please try again.");

      // Fallback to localStorage
      const stored = localStorage.getItem("financeJournals");
      if (stored) {
        try {
          setTransactionsList(JSON.parse(stored));
        } catch {
          setTransactionsList([]);
        }
      }
    } finally {
      setLoading(false);
      journalsFetching.current = false;
    }
  };

  // Load journals data on component mount (don't fetch customers here)
  useEffect(() => {
    fetchJournals();
  }, []);

  // Unified effect for both search and filter changes
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (isCRUDOperation.current) {
      isCRUDOperation.current = false;
      return;
    }

    // Debounce search to prevent excessive API calls
    const timeoutId = setTimeout(
      () => {
        fetchJournals();
      },
      searchTerm !== "" ? 500 : 0
    ); // 500ms delay for search, immediate for filter

    return () => clearTimeout(timeoutId);
  }, [searchTerm, balanceFilter]);

  // Date range effect removed as date parameters are no longer used

  // Re-transform journals when customers are loaded (only once)
  useEffect(() => {
    if (customers.length > 0 && transactionsList.length > 0) {
      // Check if we need to re-transform (only if customer names are missing)
      const needsRetransform = transactionsList.some(
        (transaction) =>
          transaction.customer === "Unknown Customer" ||
          transaction.customer ===
            `Customer ${transaction.originalData?.customer}`
      );

      if (needsRetransform) {
        // Re-transform existing journals with customer names
        const reTransformedJournals = transactionsList.map((transaction) => {
          if (transaction.originalData) {
            const customerName =
              customers.find(
                (c) => c.id === transaction.originalData.customer?.toString()
              )?.name ||
              transaction.originalData.customer_name ||
              `Customer ${transaction.originalData.customer}` ||
              "Unknown Customer";
            return {
              ...transaction,
              customer: customerName,
            };
          }
          return transaction;
        });
        setTransactionsList(reTransformedJournals);
      }
    }
  }, [customers.length]); // Only depend on customers.length, not the entire customers array

  // Save to localStorage as backup (only when transactionsList changes meaningfully)
  useEffect(() => {
    if (transactionsList.length > 0) {
      localStorage.setItem("financeJournals", JSON.stringify(transactionsList));
    }
  }, [transactionsList.length]); // Only depend on length, not the entire array

  // Handle main scroll lock when reaching transactions section
  useEffect(() => {
    let lastScrollTop =
      window.pageYOffset || document.documentElement.scrollTop;
    let ticking = false;

    const handleMainScroll = () => {
      if (!transactionsSectionRef.current || ticking) return;

      ticking = true;
      requestAnimationFrame(() => {
        const transactionsSection = transactionsSectionRef.current;
        if (!transactionsSection) {
          ticking = false;
          return;
        }

        const rect = transactionsSection.getBoundingClientRect();
        const currentScrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const scrollDirection =
          currentScrollTop > lastScrollTop ? "down" : "up";

        // When transactions section reaches the top
        if (
          rect.top <= 0 &&
          !isMainScrollLocked &&
          scrollDirection === "down"
        ) {
          setIsMainScrollLocked(true);
          // Snap to the transactions section top
          window.scrollTo({
            top: transactionsSection.offsetTop,
            behavior: "auto",
          });
        }
        // Allow scrolling back up when section is above viewport
        else if (rect.top > 0 && isMainScrollLocked) {
          setIsMainScrollLocked(false);
        }

        lastScrollTop = currentScrollTop;
        ticking = false;
      });
    };

    const handleWheel = (e) => {
      if (!transactionsSectionRef.current || !isMainScrollLocked) return;

      const transactionsSection = transactionsSectionRef.current;
      const rect = transactionsSection.getBoundingClientRect();
      const tableBody = tableBodyRef.current;

      if (!tableBody) return;

      const { scrollTop, scrollHeight, clientHeight } = tableBody;
      const isTableAtTop = scrollTop === 0;
      const isTableAtBottom = scrollTop + clientHeight >= scrollHeight - 1;

      // Scrolling down
      if (e.deltaY > 0) {
        if (rect.top <= 0 && !isTableAtBottom) {
          // Table can scroll, prevent main scroll
          e.preventDefault();
          e.stopPropagation();
          tableBody.scrollTop += e.deltaY;
        } else if (isTableAtBottom && rect.top <= 0) {
          // Table at bottom, allow main scroll
          setIsMainScrollLocked(false);
        }
      }
      // Scrolling up
      else if (e.deltaY < 0) {
        if (!isTableAtTop) {
          // Table can scroll up, prevent main scroll
          e.preventDefault();
          e.stopPropagation();
          tableBody.scrollTop += e.deltaY;
        } else if (isTableAtTop) {
          // Table at top, allow main scroll up
          setIsMainScrollLocked(false);
        }
      }
    };

    window.addEventListener("scroll", handleMainScroll, { passive: true });
    window.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      window.removeEventListener("scroll", handleMainScroll);
      window.removeEventListener("wheel", handleWheel);
    };
  }, [isMainScrollLocked]);

  // Handle table body scroll for pagination visibility
  useEffect(() => {
    // Clean up previous listener if exists
    if (scrollCleanupRef.current) {
      scrollCleanupRef.current();
      scrollCleanupRef.current = null;
    }

    const setupScrollListener = () => {
      const tableBody = tableBodyRef.current;
      if (!tableBody) {
        // Retry after a short delay if table body is not ready
        setTimeout(setupScrollListener, 50);
        return;
      }

      const handleTableScroll = () => {
        const { scrollTop, scrollHeight, clientHeight } = tableBody;
        // Check if table is scrollable
        const isScrollable = scrollHeight > clientHeight;

        if (!isScrollable) {
          // If table is not scrollable, hide pagination (no need to scroll)
          setShowPagination(false);
          return;
        }

        // Calculate if we're at the bottom (with a small threshold)
        const threshold = 5;
        const scrollBottom = scrollTop + clientHeight;
        const isAtBottom = scrollBottom >= scrollHeight - threshold;

        // Show pagination ONLY when reaching the end, hide otherwise
        if (isAtBottom) {
          setShowPagination(true);
        } else {
          setShowPagination(false);
        }
      };

      // Check initial scroll position - start with pagination hidden
      setShowPagination(false);
      handleTableScroll();

      tableBody.addEventListener("scroll", handleTableScroll, {
        passive: true,
      });

      // Store cleanup function
      scrollCleanupRef.current = () => {
        tableBody.removeEventListener("scroll", handleTableScroll);
      };
    };

    // Setup with a small delay to ensure DOM is ready
    const timeoutId = setTimeout(setupScrollListener, 100);

    return () => {
      clearTimeout(timeoutId);
      if (scrollCleanupRef.current) {
        scrollCleanupRef.current();
        scrollCleanupRef.current = null;
      }
    };
  }, [transactionsList.length, currentPage]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    // Validation
    if (!formData.customerId) {
      toast.error("Please select a customer");
      return;
    }

    if (!formData.debit && !formData.credit) {
      toast.error("Please enter either debit or credit amount");
      return;
    }

    if (formData.debit && formData.credit) {
      toast.error("Please enter either debit or credit amount, not both");
      return;
    }

    const selectedCustomer = customers.find(
      (c) => String(c.id) === String(formData.customerId)
    );
    if (!selectedCustomer) {
      toast.error("Selected customer is invalid. Please try again.");
      return;
    }

    const amountValue = parseFloat(formData.debit || formData.credit || 0);
    if (!amountValue || amountValue <= 0) {
      toast.error("Amount must be greater than 0");
      return;
    }

    if (formData.credit) {
      const limitError = getJournalCreditLimitError(
        selectedCustomer,
        formData.credit
      );
      if (limitError) {
        toast.error(limitError);
        return;
      }
    }

    const transactionType = formData.debit ? "debit" : "credit";
    const transactionDate = selectedDate || new Date();

    setLoading(true);
    try {
      // Format date to YYYY-MM-DD without timezone conversion
      const year = transactionDate.getFullYear();
      const month = String(transactionDate.getMonth() + 1).padStart(2, "0");
      const day = String(transactionDate.getDate()).padStart(2, "0");
      const formattedDate = `${year}-${month}-${day}`;

      const journalData = {
        title:
          transactionType === "debit"
            ? "Debit Transaction"
            : "Credit Transaction",
        tnx_id: `TRN_${Date.now().toString().slice(-6)}`,
        tnx_date: formattedDate,
        customer: parseInt(selectedCustomer.id, 10),
        customer_name: selectedCustomer.name,
        amount: amountValue,
        tnx_type: transactionType,
        note: formData.note || "",
      };

      await financeJournalAPI.createJournal(journalData);

      toast.success("Journal entry created successfully!");

      // Save last used date
      setLastUsedDate(transactionDate);

      // Refresh data
      isCRUDOperation.current = true;
      await fetchJournals();

      // Clear form
      handleClear();
    } catch (error) {
      console.error("Failed to create journal entry:", error);
      const data = error.response?.data;
      const fieldErrors = data?.errors || data?.error || {};
      const amountError = fieldErrors?.amount;
      const errorMessage =
        (Array.isArray(amountError) ? amountError[0] : amountError) ||
        data?.message ||
        data?.detail ||
        error.message ||
        "Failed to create journal entry. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFormData({
      date: selectedDate.toLocaleDateString("en-GB"),
      customerId: "",
      note: "",
      debit: "",
      credit: "",
    });
  };

  const handleCustomerSelect = (customerId) => {
    setFormData((prev) => ({
      ...prev,
      customerId: customerId,
    }));
    setIsCustomerDropdownOpen(false);
    setCustomerSearchTerm("");
  };

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (isCustomerDropdownOpen && customerSearchInputRef.current) {
      customerSearchInputRef.current.focus();
    }
  }, [isCustomerDropdownOpen]);

  // Debounced customer search effect
  useEffect(() => {
    if (!isCustomerDropdownOpen) return;

    const timeoutId = setTimeout(() => {
      fetchCustomers(customerSearchTerm);
      setHighlightedIndex(0);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [customerSearchTerm, isCustomerDropdownOpen]);

  const selectedCustomer = customers.find((c) => c.id === formData.customerId);

  const formatCustomerCreditLimit = (limit) => {
    // null = no limit set
    if (limit === null || limit === undefined) return null;
    const amount = Number(limit);
    if (Number.isNaN(amount)) return null;
    return `€${amount.toFixed(2)}`;
  };

  // Matches backend check_customer_credit_limit:
  // - credit_limit null → no check
  // - else convert to number and block if balance - credit < -limit
  const getJournalCreditLimitError = (customer, creditRaw) => {
    if (!customer) return "";

    // Only enforce when credit_limit is not null
    if (customer.credit_limit === null || customer.credit_limit === undefined) {
      return "";
    }

    const limit = Number(customer.credit_limit);
    if (Number.isNaN(limit) || !Number.isFinite(limit)) return "";

    const creditAmount = parseFloat(creditRaw);
    if (!creditRaw || Number.isNaN(creditAmount) || creditAmount <= 0) return "";

    const base = Number(customer.balance ?? 0);
    if (Number.isNaN(base)) return "";

    const balanceAfter = base - creditAmount;
    if (balanceAfter >= -limit) return "";

    const outstandingAfter = Math.max(0, -balanceAfter);
    return `Credit limit exceeded. Limit: €${limit.toFixed(
      2
    )}, outstanding after this credit: €${outstandingAfter.toFixed(2)}.`;
  };

  const creditLimitError = getJournalCreditLimitError(
    selectedCustomer,
    formData.credit
  );
  const isSaveDisabled = Boolean(loading || creditLimitError);

  const getCustomerOptionLabel = (customer) => {
    if (!customer) return "";
    return `${customer.id} ${customer.name}`;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isCustomerDropdownOpen &&
        !event.target.closest(".journal-customer-dropdown-container")
      ) {
        setIsCustomerDropdownOpen(false);
      }
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setShowDatePicker(false);
      }
      if (
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(event.target)
      ) {
        setShowFilterDropdown(false);
      }
    };

    if (isCustomerDropdownOpen || showDatePicker || showFilterDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isCustomerDropdownOpen, showDatePicker, showFilterDropdown]);

  // Date picker functions
  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(23, 59, 59, 999); // End of today
    return date > today;
  };

  const isDateSelected = (date) => {
    if (!selectedDate) return false;
    return date.getTime() === selectedDate.getTime();
  };

  const handleDateClick = (date) => {
    if (isDateDisabled(date)) return;
    setSelectedDate(date);
    setShowDatePicker(false);
  };

  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const formatDate = () => {
    return selectedDate
      ? selectedDate.toLocaleDateString("en-GB")
      : new Date().toLocaleDateString("en-GB");
  };

  // Filter options and functions
  const filterOptions = [
    { value: "all", label: "All" },
    { value: "credit", label: "Credit" },
    { value: "debit", label: "Debit" },
  ];

  const handleFilterSelect = (value) => {
    setBalanceFilter(value);
    setShowFilterDropdown(false);
    // The useEffect with [balanceFilter] dependency will handle the API call
  };

  const getFilterLabel = () => {
    const option = filterOptions.find((opt) => opt.value === balanceFilter);
    return option ? option.label : "All";
  };

  // Delete modal handlers
  const handleDeleteClick = (transaction) => {
    setDeletingTransaction(transaction);
    setShowDeleteModal(true);
  };

  const handleDeleteTransaction = async (transactionId) => {
    setLoading(true);
    try {
      // Find the transaction to get the original API ID
      const transaction = transactionsList.find((t) => t.id === transactionId);
      const apiId = transaction?.originalData?.id || transactionId;

      await financeJournalAPI.deleteJournal(apiId);
      toast.success("Journal entry deleted successfully!");

      // Refresh data
      isCRUDOperation.current = true;
      await fetchJournals();
    } catch (error) {
      toast.error("Failed to delete journal entry. Please try again.");

      // Fallback to localStorage
      setTransactionsList((prev) =>
        prev.filter((transaction) => transaction.id !== transactionId)
      );
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setDeletingTransaction(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDeletingTransaction(null);
  };

  // Edit modal handlers
  const handleEditClick = (transaction) => {
    setEditingTransaction(transaction);
    setShowEditModal(true);
  };

  const handleEditTransaction = async (updatedTransaction) => {
    setLoading(true);
    try {
      // Find the original transaction to get the API ID
      const originalTransaction = transactionsList.find(
        (t) => t.id === updatedTransaction.id
      );
      const apiId =
        originalTransaction?.originalData?.id || updatedTransaction.id;

      const selectedCustomerForEdit =
        customers.find(
          (c) => c.id === updatedTransaction.customerId?.toString()
        ) ||
        customers.find(
          (c) => c.id === updatedTransaction.originalData?.customer?.toString()
        );

      const journalData = {
        title:
          updatedTransaction.debit > 0
            ? "Debit Transaction"
            : "Credit Transaction",
        tnx_id:
          updatedTransaction.originalData?.tnx_id || updatedTransaction.id,
        tnx_date:
          updatedTransaction.originalData?.tnx_date ||
          new Date().toISOString().split("T")[0],
        customer: parseInt(
          selectedCustomerForEdit?.id ||
            updatedTransaction.customerId ||
            updatedTransaction.originalData?.customer,
          10
        ),
        customer_name:
          selectedCustomerForEdit?.name || updatedTransaction.customer,
        amount: parseFloat(
          updatedTransaction.debit || updatedTransaction.credit
        ),
        tnx_type: updatedTransaction.debit > 0 ? "debit" : "credit",
        note: updatedTransaction.note,
      };

      await financeJournalAPI.updateJournal(apiId, journalData);
      toast.success("Journal entry updated successfully!");

      // Refresh data
      isCRUDOperation.current = true;
      await fetchJournals();
      setShowEditModal(false);
      setEditingTransaction(null);
    } catch (error) {
      console.error("Failed to update journal entry:", error);
      const data = error.response?.data;
      const fieldErrors = data?.errors || data?.error || {};
      const amountError = fieldErrors?.amount;
      const errorMessage =
        (Array.isArray(amountError) ? amountError[0] : amountError) ||
        data?.message ||
        data?.detail ||
        error.message ||
        "Failed to update journal entry. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setShowEditModal(false);
    setEditingTransaction(null);
  };

  // Sorting toggle function: null -> 'asc' -> 'desc' -> null
  const toggleSort = (currentSort, setSortFn, otherSetSortFn) => {
    // Reset other column sort
    otherSetSortFn(null);
    
    if (currentSort === null) {
      setSortFn('asc');
    } else if (currentSort === 'asc') {
      setSortFn('desc');
    } else {
      setSortFn(null);
    }
  };

  const handleCustomerSort = () => {
    toggleSort(customerSort, setCustomerSort, setAmountSort);
  };

  const handleAmountSort = () => {
    toggleSort(amountSort, setAmountSort, setCustomerSort);
  };

  // Since we're now using server-side filtering, we use transactionsList directly
  // Apply sorting if active
  const getSortedTransactions = () => {
    let sorted = [...transactionsList];
    
    if (customerSort) {
      sorted.sort((a, b) => {
        const nameA = (a.customer || '').toLowerCase();
        const nameB = (b.customer || '').toLowerCase();
        if (customerSort === 'asc') {
          return nameA.localeCompare(nameB);
        } else {
          return nameB.localeCompare(nameA);
        }
      });
    } else if (amountSort) {
      sorted.sort((a, b) => {
        // Get the amount value (positive for debit, negative for credit for comparison)
        const getAmount = (t) => {
          if (t.originalData?.tnx_type === 'debit') {
            return parseFloat(t.originalData.amount || 0);
          } else if (t.originalData?.tnx_type === 'credit') {
            return -parseFloat(t.originalData.amount || 0);
          }
          return 0;
        };
        const amountA = getAmount(a);
        const amountB = getAmount(b);
        if (amountSort === 'asc') {
          return amountA - amountB;
        } else {
          return amountB - amountA;
        }
      });
    }
    
    return sorted;
  };

  const filteredTransactions = getSortedTransactions();

  // Apply pagination
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTransactions = filteredTransactions.slice(
    startIndex,
    endIndex
  );

  return (
    <>
      {showDailyReport ? (
        <DailyReportView />
      ) : (
        <div
          className={`journal-container ${
            isMainScrollLocked ? "scroll-locked" : ""
          }`}
        >
          {/* Transaction Form */}
          <div className="transaction-form">
            {/* Header */}
            <div className="journal-header">
              <div className="journal-title-section">
                <h1 className="journal-title">Journal</h1>
                <p className="journal-subtitle">
                  Manage transactions and track financial data
                </p>
              </div>
              <button
                className={`${
                  isPartsURL
                    ? "daily-report-btn-parts"
                    : isAccessoriesURL
                    ? "daily-report-btn-acc"
                    : "daily-report-btn"
                }`}
                onClick={() => setShowDailyReport(true)}
              >
                <DailyReportIcon
                  fill={
                    isPartsURL
                      ? "var(--primary-color-parts)"
                      : isAccessoriesURL
                      ? "var(--primary-color-acc)"
                      : "var(--primary-color-mobile)"
                  }
                />
                Daily Report
              </button>
            </div>

            <div className="form-row">
              {/* Date picker */}
              <div className="form-group">
                <label className="journal-view-form-label">Date *</label>
                <div className="journal-date-range" ref={datePickerRef}>
                  <Calendar size={16} className="journal-date-icon" />
                  <input
                    type="text"
                    className={`${
                      isPartsURL
                        ? "journal-date-input-parts"
                        : isAccessoriesURL
                        ? "journal-date-input-acc"
                        : "journal-date-input"
                    }`}
                    value={formatDate()}
                    onClick={() => setShowDatePicker(!showDatePicker)}
                  />

                  {/* Date Picker Popup */}
                  {showDatePicker && (
                    <>
                      <div
                        className="journal-date-picker-backdrop"
                        onClick={() => setShowDatePicker(false)}
                      ></div>
                      <div className="journal-date-picker">
                        <div className="journal-date-picker-header">
                          <button
                            className="journal-date-picker-nav-btn"
                            onClick={() => navigateMonth(-1)}
                          >
                            <ChevronDown
                              size={16}
                              style={{ transform: "rotate(90deg)" }}
                            />
                          </button>
                          <h3 className="journal-date-picker-month-year">
                            {currentMonth.toLocaleDateString("en-US", {
                              month: "long",
                              year: "numeric",
                            })}
                          </h3>
                          <button
                            className="journal-date-picker-nav-btn"
                            onClick={() => navigateMonth(1)}
                          >
                            <ChevronDown
                              size={16}
                              style={{ transform: "rotate(-90deg)" }}
                            />
                          </button>
                        </div>

                        <div className="journal-date-picker-weekdays">
                          <div className="journal-weekday">Su</div>
                          <div className="journal-weekday">Mo</div>
                          <div className="journal-weekday">Tu</div>
                          <div className="journal-weekday">We</div>
                          <div className="journal-weekday">Th</div>
                          <div className="journal-weekday">Fr</div>
                          <div className="journal-weekday">Sa</div>
                        </div>

                        <div
                          className={`${
                            isPartsURL
                              ? "journal-date-picker-days-parts"
                              : isAccessoriesURL
                              ? "journal-date-picker-days-acc"
                              : "journal-date-picker-days"
                          }`}
                        >
                          {getDaysInMonth(currentMonth).map((day, index) => (
                            <div
                              key={index}
                              className={`${
                                isPartsURL
                                  ? "journal-date-picker-day-parts"
                                  : isAccessoriesURL
                                  ? "journal-date-picker-day-acc"
                                  : "journal-date-picker-day"
                              } ${day ? "" : "empty"} ${
                                day && isDateDisabled(day) ? "disabled" : ""
                              } ${
                                day && isDateSelected(day) ? "selected" : ""
                              }`}
                              onClick={() => day && handleDateClick(day)}
                            >
                              {day ? day.getDate() : ""}
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Customer dropdown */}
              <div className="form-group">
                <label className="journal-view-form-label">Customer</label>
                <div className="journal-customer-dropdown-container">
                  <div
                    className={`${
                      isPartsURL
                        ? "journal-dropdown-input-parts"
                        : isAccessoriesURL
                        ? "journal-dropdown-input-acc"
                        : "journal-dropdown-input"
                    }`}
                    onClick={() => {
                      if (!isCustomerDropdownOpen) {
                        fetchCustomers("");
                      }
                      setIsCustomerDropdownOpen(!isCustomerDropdownOpen);
                    }}
                  >
                    <span
                      className={
                        selectedCustomer
                          ? "journal-selected-value"
                          : "journal-not-selected-value"
                      }
                    >
                      {selectedCustomer
                        ? getCustomerOptionLabel(selectedCustomer)
                        : "Select customer"}
                    </span>
                    <ChevronDown className="journal-dropdown-arrow" size={16} />
                  </div>

                  {isCustomerDropdownOpen && (
                    <div className="journal-dropdown-menu">
                      <div className="journal-dropdown-search">
                        <Search className="journal-search-icon" size={16} />
                        <input
                          ref={customerSearchInputRef}
                          type="text"
                          className={`${
                            isPartsURL
                              ? "journal-search-input-parts"
                              : isAccessoriesURL
                              ? "journal-search-input-acc"
                              : "journal-search-input"
                          }`}
                          placeholder="Search customer name..."
                          value={customerSearchTerm}
                          onChange={(e) =>
                            setCustomerSearchTerm(e.target.value)
                          }
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && customers.length > 0) {
                              handleCustomerSelect(
                                customers[highlightedIndex]?.id
                              );
                            }
                          }}
                        />
                      </div>
                      <div className="journal-dropdown-list noscrollbar">
                        {customersLoading ? (
                          <div className="journal-dropdown-item journal-no-results">
                            Loading...
                          </div>
                        ) : customers.length === 0 ? (
                          <div className="journal-dropdown-item journal-no-results">
                            No customers found
                          </div>
                        ) : (
                          customers.map((customer, index) => (
                            <div
                              key={customer.id}
                              className={`journal-dropdown-item ${
                                index === highlightedIndex ? "highlighted" : ""
                              }`}
                              onClick={() => handleCustomerSelect(customer.id)}
                              onMouseEnter={() => setHighlightedIndex(index)}
                            >
                              {getCustomerOptionLabel(customer)}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {selectedCustomer &&
                  selectedCustomer.credit_limit !== null &&
                  selectedCustomer.credit_limit !== undefined && (
                    <p className="journal-customer-credit-limit-hint">
                      Credit limit:{" "}
                      {formatCustomerCreditLimit(selectedCustomer.credit_limit)}
                      {Number(selectedCustomer.credit_limit) === 0
                        ? " (no credit)"
                        : ""}
                    </p>
                  )}
              </div>

              {/* Debit input */}
              <div
                className={` ${
                  isPartsURL
                    ? "form-group-parts amount-group"
                    : isAccessoriesURL
                    ? "form-group-acc amount-group"
                    : "form-group amount-group"
                }`}
              >
                <label className="journal-view-form-label">
                  Debit <CreditIcon />
                </label>
                <input
                  type="text"
                  name="debit"
                  value={formData.debit}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData((prev) => ({
                      ...prev,
                      debit: value,
                      credit: value ? "" : prev.credit, // Clear credit when debit has value
                    }));
                  }}
                  placeholder="€0.0"
                  disabled={formData.credit && formData.credit !== ""}
                />
              </div>

              {/* Credit input */}
              <div
                className={`${
                  isPartsURL
                    ? "form-group-parts amount-group"
                    : isAccessoriesURL
                    ? "form-group-acc amount-group"
                    : "form-group amount-group"
                }`}
              >
                <label className="journal-view-form-label">
                  Credit <DebitIcon />
                </label>
                <input
                  type="text"
                  name="credit"
                  value={formData.credit}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData((prev) => ({
                      ...prev,
                      credit: value,
                      debit: value ? "" : prev.debit, // Clear debit when credit has value
                    }));
                  }}
                  placeholder="€0.0"
                  disabled={formData.debit && formData.debit !== ""}
                  className={creditLimitError ? "journal-input-error" : ""}
                />
                {creditLimitError && (
                  <span className="journal-field-error">{creditLimitError}</span>
                )}
              </div>
            </div>

            {/* Note input */}
            <div className="note-input-row">
              <div
                className={`${
                  isPartsURL
                    ? "note-input-group-parts"
                    : isAccessoriesURL
                    ? "note-input-group-acc"
                    : "note-input-group"
                }`}
              >
                <label className="journal-view-form-label">Note</label>
                <input
                  type="text"
                  name="note"
                  value={formData.note}
                  onChange={handleInputChange}
                  placeholder="Add your note optional..."
                />
              </div>
            </div>

            <div className="form-actions">
              <button
                className={`${
                  isPartsURL
                    ? "clear-btn-parts"
                    : isAccessoriesURL
                    ? "clear-btn-acc"
                    : "clear-btn"
                }`}
                onClick={handleClear}
              >
                Clear
              </button>
              <button
                className={`${
                  isPartsURL
                    ? "save-btn-parts"
                    : isAccessoriesURL
                    ? "save-btn-acc"
                    : "save-btn"
                }`}
                onClick={handleSave}
                disabled={isSaveDisabled}
                title={creditLimitError || undefined}
              >
                Save
              </button>
            </div>
          </div>

          {/* Transactions Card */}
          <div
            id="transactionsection"
            className="transactions-section"
            ref={transactionsSectionRef}
          >
            <div className="transactions-header">
              <h2 className="transactions-title">All Transaction</h2>
              <div className="transactions-controls">
                <div
                  className={`${
                    isPartsURL
                      ? "search-wrapper-parts"
                      : isAccessoriesURL
                      ? "search-wrapper-acc"
                      : "search-wrapper"
                  }`}
                >
                  <Search className="search-icon" size={16} />
                  <input
                    type="text"
                    className="transactions-search"
                    placeholder="Search by customer name or amount..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                <div
                  className="journal-filter-dropdown"
                  ref={filterDropdownRef}
                >
                  <button
                    className={`${
                      isPartsURL
                        ? "journal-filter-button-parts"
                        : isAccessoriesURL
                        ? "journal-filter-button-acc"
                        : "journal-filter-button"
                    }`}
                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <FilterIcon size={16} />
                      {getFilterLabel()}
                    </div>

                    <ChevronDown size={16} />
                  </button>

                  <div
                    className={`journal-filter-dropdown-content ${
                      showFilterDropdown ? "show" : ""
                    }`}
                  >
                    {filterOptions.map((option) => (
                      <div
                        key={option.value}
                        className={`journal-filter-dropdown-item ${
                          balanceFilter === option.value ? "selected" : ""
                        }`}
                        onClick={() => handleFilterSelect(option.value)}
                      >
                        <Check
                          size={16}
                          className="journal-filter-dropdown-check"
                        />
                        {option.label}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Transactions Table */}
            <div className="journal-table-container">
              <div className="journal-table-responsive">
                <table className="journal-table">
                  <thead>
                    <tr>
                      <th>TRN ID</th>
                      <th 
                        onClick={handleCustomerSort}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        title={customerSort === null ? 'Click to sort ascending' : customerSort === 'asc' ? 'Click to sort descending' : 'Click to reset'}
                      >
                        CUSTOMER <ArrowDownUpIcon sortDirection={customerSort} color={ customerSort ? isPartsURL ? 'var(--primary-color-parts)' : isAccessoriesURL ? 'var(--primary-color-acc)' : 'var(--primary-color-mobile)' : '#57606A'} />
                      </th>
                      <th>NOTE</th>
                      <th 
                        onClick={handleAmountSort}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        title={amountSort === null ? 'Click to sort ascending' : amountSort === 'asc' ? 'Click to sort descending' : 'Click to reset'}
                      >
                        CREDIT / DEBIT <ArrowDownUpIcon sortDirection={amountSort} color={ amountSort ? isPartsURL ? 'var(--primary-color-parts)' : isAccessoriesURL ? 'var(--primary-color-acc)' : 'var(--primary-color-mobile)' : '#57606A'} />
                      </th>
                      <th>BALANCE</th>
                      <th style={{ textAlign: "end", paddingRight: "30px" }}>
                        ACTION
                      </th>
                    </tr>
                  </thead>
                  <tbody ref={tableBodyRef} style={{ height: "100%" }}>
                    {paginatedTransactions.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="no-products-message">
                          <div
                            className=""
                            style={{
                              marginBottom: "4rem",
                              marginTop: "4rem",
                            }}
                          >
                            <NoDataIcon />
                            <h3 style={{ marginTop: "2rem" }}>
                              No transactions found
                            </h3>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      paginatedTransactions.map((transaction, index) => (
                        <tr key={transaction.id}>
                          <td className="journal-trn-id">{transaction.id}</td>
                          <td
                            className="journal-customer-name"
                            title={transaction.customer}
                          >
                            {transaction.customer}
                          </td>
                          <td className="journal-note" title={transaction.note}>
                            {transaction.note}
                          </td>
                          <td className="journal-amount">
                            {transaction.originalData?.tnx_type ===
                              "credit" && (
                              <span className="journal-credit-amount">
                                -€
                                {parseFloat(
                                  transaction.originalData.amount || 0
                                ).toFixed(2)}
                              </span>
                            )}
                            {transaction.originalData?.tnx_type === "debit" && (
                              <span className="journal-debit-amount">
                                +€
                                {parseFloat(
                                  transaction.originalData.amount || 0
                                ).toFixed(2)}
                              </span>
                            )}
                          </td>

                          <td className="journal-balance">
                            <span
                              className={`balance-badge ${
                                transaction.balance < 0
                                  ? "negative"
                                  : "positive"
                              }`}
                            >
                              €{transaction.balance.toFixed(2)}
                            </span>
                          </td>

                          <td className="journal-actions">
                            <div className="journal-action-buttons">
                              <button
                                className={` ${
                                  isPartsURL
                                    ? "journal-action-btn-parts journal-view"
                                    : isAccessoriesURL
                                    ? "journal-action-btn-acc journal-view"
                                    : "journal-action-btn journal-view"
                                }`}
                                title="Edit transaction"
                                onClick={() => handleEditClick(transaction)}
                              >
                                <EditIcon
                                  fill={`${
                                    isPartsURL
                                      ? "var(--primary-color-parts)"
                                      : isAccessoriesURL
                                      ? "var(--primary-color-acc)"
                                      : "var(--primary-color-mobile)"
                                  }`}
                                  disabled={false}
                                />
                              </button>
                              <button
                                className="journal-action-btn journal-delete"
                                title="Delete transaction"
                                onClick={() => handleDeleteClick(transaction)}
                              >
                                <RedDeleteIcon />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {/* {filteredTransactions.length > itemsPerPage && showPagination && ( */}
            {filteredTransactions.length > itemsPerPage && (
              <div className="pagination-wrapper">
                <span className="pagination-info">
                  Page {currentPage} of{" "}
                  {Math.ceil(filteredTransactions.length / itemsPerPage)}
                </span>
                <div className="pagination-controls">
                  <button
                    className={`${
                      isPartsURL
                        ? "pagination-btn-parts"
                        : isAccessoriesURL
                        ? "pagination-btn-acc"
                        : "pagination-btn"
                    }`}
                    disabled={currentPage === 1}
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                  >
                    Previous
                  </button>
                  <button
                    className={`${
                      isPartsURL
                        ? "pagination-btn-next-parts"
                        : isAccessoriesURL
                        ? "pagination-btn-next-acc"
                        : "pagination-btn-next"
                    }`}
                    disabled={
                      currentPage >=
                      Math.ceil(filteredTransactions.length / itemsPerPage)
                    }
                    onClick={() =>
                      setCurrentPage((prev) =>
                        Math.min(
                          Math.ceil(filteredTransactions.length / itemsPerPage),
                          prev + 1
                        )
                      )
                    }
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {/* Edit Modal */}
            {showEditModal && editingTransaction && (
              <JournalEditModal
                isOpen={showEditModal}
                onClose={handleCancelEdit}
                transaction={editingTransaction}
                customers={customers}
                onSave={handleEditTransaction}
              />
            )}

            {/* Delete Modal */}
            {showDeleteModal && deletingTransaction && (
              <>
                <div className="modal-overlay">
                  <div className="delete-modal">
                    <div className="modal-icon">
                      <img src={DeleteIcon} alt="Delete" />
                    </div>
                    <h3 className="modal-title">Delete Transaction</h3>
                    <p className="modal-message">
                      Are you sure you want to delete transaction{" "}
                      <b>{deletingTransaction.id}</b>?
                      <br />
                      This action cannot be undone.
                    </p>
                    <div className="modal-actions">
                      <button
                        className="modal-cancel-btn"
                        onClick={handleCancelDelete}
                      >
                        Cancel
                      </button>
                      <button
                        className="modal-delete-btn"
                        onClick={() =>
                          handleDeleteTransaction(deletingTransaction.id)
                        }
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default JournalView;
